// WHO an email is from, as a person reads it (busy-my-web#361, Saffet 2026-09-03: "Can we put the
// name of the company like Gmail not the email address").
//
// The rule is one sentence and it is the same one the BFF already applies on its side of the wire
// (MainBff's `GlobalTimelineBuilder.DisplayName` / acc-account-sync-srv's `EmailService.SenderName`):
// the DISPLAY NAME the mail carries, and the bare address only when it carries none. Nothing is
// synthesised — a local part is not a person, so "no.reply@accounts.google.com" stays exactly that
// rather than being dressed up as "No Reply".
//
// It lives here, alone, because five surfaces were each parsing the same header with their own
// regex — the mailbox listing, the Briefing's thread rows, the Email Workspace's group and email
// rows, the possible-match cards and the story's thread steps — and five copies of one rule is five
// chances for two rows to disagree about who wrote the same email.
//
// The shapes that reach it:
//   • an RFC header string, as the API stores it: `"Gmail" <no-reply@accounts.google.com>`, and
//     sometimes several of them, comma- or semicolon-separated (`recipientsTo`);
//   • a participant object, `{ name, email }` (the timeline's shape) or `{ name, address }`
//     (the `email-addresses` parse) — see `participantDisplay`.

/**
 * Every address in a header, and `firstEntry` — the FIRST of them.
 *
 * A row has one line, so a header naming three people is read by its first; the thread's own view is
 * where the rest belong. The split is on separators OUTSIDE quotes, because a quoted display name is
 * allowed to contain one (`"Ahmadi, Fatemeh" <f@x.com>`) and cutting there would leave half a
 * surname on the row.
 */
function entries(header) {
  const raw = String(header ?? '')
  const out = []
  let quoted = false
  let start = 0
  for (let i = 0; i < raw.length; i += 1) {
    const ch = raw[i]
    if (ch === '"') quoted = !quoted
    else if (!quoted && (ch === ',' || ch === ';')) { out.push(raw.slice(start, i)); start = i + 1 }
  }
  out.push(raw.slice(start))
  return out
}

const firstEntry = (header) => entries(header)[0]

/**
 * The bare address out of `Name <a@b.com>` — the whole string when there are no angle brackets,
 * because a header that is only an address IS the address.
 */
export function senderAddress(raw) {
  const first = firstEntry(raw).trim()
  const angled = first.match(/<([^>]*)>/)
  return (angled ? angled[1] : first).trim()
}

/**
 * The name to PRINT: the display name where the mail carries one, the address where it does not.
 *
 * `"Gmail" <no-reply@accounts.google.com>` → `Gmail`; `<no-reply@x.com>` and `no-reply@x.com` → the
 * address; an empty or absent header → the empty string, so a caller can fall back rather than draw
 * a blank cell.
 */
export function senderDisplayName(raw) {
  const first = firstEntry(raw).trim()
  if (!first) return ''
  const open = first.indexOf('<')
  if (open > 0) {
    const name = first.slice(0, open).trim().replace(/^"+|"+$/g, '').trim()
    if (name) return name
  }
  return senderAddress(first)
}

/**
 * A PARTICIPANT, named.
 *
 * The estate's own contact record wins where there is one (that is the name the user gave this
 * person), then whatever the payload calls the name, then the address. Every candidate goes through
 * `senderDisplayName` because any of them can arrive as a whole header: the sync writes the raw
 * `Senders` string into more than one field, and a cell that prints `Gmail <no-reply@…>` is the very
 * thing #361 is about.
 */
//
// …and where neither carries a name, the HEADERS of the thread's own mail are asked (busy-my-web#611,
// Saffet 2026-10-09: "hello@wefu…" where Gmail shows "Wefunder"). A participant is the sync's
// CONTACT, and a contact is named once, by the first mail that mentioned the address — a bare `Cc:`
// or a `Sender:` line with no display name leaves it nameless for good, while the very next message's
// `From:` says `"Wefunder" <hello@wefunder.com>`. `names` is that header map (`messageNames`), keyed
// by lower-cased address; a name that is only the address again counts as no name.
export function participantDisplay(participant, resolveContact = null, names = null) {
  const p = participant || {}
  const contact = typeof resolveContact === 'function' && p.id ? resolveContact(p.id) : null
  const named = senderDisplayName(contact?.name || p.name || '')
  const address = senderAddress(p.email || p.address || p.emailAddress || contact?.emailAddress || '')
  if (named && !isAddressOnly(named, address)) return named
  const fromHeaders = names instanceof Map ? names.get(address.toLowerCase()) : ''
  if (fromHeaders) return fromHeaders
  return named || senderDisplayName(address)
}

/** Is this "name" really just the address (or any address at all)? Then it names nobody. */
function isAddressOnly(name, address) {
  const n = String(name || '').trim().toLowerCase()
  return !!n && (n === String(address || '').trim().toLowerCase() || /^[^\s@]+@[^\s@]+$/.test(n))
}

/**
 * Every DISPLAY NAME a set of address headers carries, by lower-cased address (busy-my-web#611).
 *
 * `["\"Wefunder\" <hello@wefunder.com>", "Tom <t@x.com>; <a@y.com>"]` → hello@wefunder.com → Wefunder,
 * t@x.com → Tom. An entry with no display part adds nothing, and the first name heard for an address
 * is kept — the same address named two ways in one thread is rare, and the earlier header is as true.
 */
export function headerNames(headers) {
  const names = new Map()
  for (const header of headers || []) {
    for (const entry of entries(header)) {
      const address = senderAddress(entry).toLowerCase()
      const name = senderDisplayName(entry)
      if (!address || !name || isAddressOnly(name, address) || names.has(address)) continue
      names.set(address, name)
    }
  }
  return names
}

/** …read off a thread's messages: their From, To and Cc lines (`senders`, `recipientsTo`, `recipientsCc`). */
export function messageNames(messages) {
  return headerNames((messages || [])
    .flatMap((m) => (m ? [m.senders, m.from, m.recipientsTo, m.recipientsCc] : []))
    .filter((h) => typeof h === 'string'))
}

export default senderDisplayName
